import FormatCurrency from 'components/Common/FormatCurrency'
import { useIntl } from 'react-intl'
import BodyText from 'LuxKit/Typography/BodyText'
import CurrencyContext from 'contexts/currencyContext'
import { useAppSelector } from 'hooks/reduxHooks'
import { rem } from 'polished'
import React, { useCallback, useContext } from 'react'
import styled from 'styled-components'
import NavBarButtonBase from '../NavBarButton/NavBarButtonBase'
import NavBarUserAvatar from '../NavBar/NavBarUserAvatar'
import FormatLoyaltyPoints from 'loyalty/components/Formatters/FormatLoyaltyPoints'
import AccountMenuDrawer from '../AccountMenu/AccountMenuDrawer'
import ModalContext from 'contexts/ModalContext'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import useCurrentLoyaltyTier from 'loyalty/hooks/useCurrentLoyaltyTier'
import getLoyaltyTierDisplayName from 'loyalty/selectors/getLoyaltyTierDisplayName'
import getHasLoyaltyAccount from 'loyalty/selectors/getHasLoyaltyAccount'
import { mediaHoverable } from 'lib/theme/mediaQueries'

const FLIPPING_ROW_HEIGHT = rem(16)

const FlippingRow = styled(BodyText)`
  transition: transform 300ms;
`

const FlippingContainer = styled.div`
  display: grid;
  grid-template: repeat(2, ${FLIPPING_ROW_HEIGHT}) / minmax(0, 1fr);
  position: relative;
  overflow: hidden;
  height: ${FLIPPING_ROW_HEIGHT};
`

const Button = styled(NavBarButtonBase)`
  padding: ${rem(12)};
  padding-left: ${rem(8)};
  display: flex;
  gap: ${rem(8)};
  align-items: center;
  text-align: start;
  max-width: ${rem(144)};
  transition: background-color 0.2s ease;

  ${mediaHoverable} {
    &:hover {
      background-color: ${props => props.theme.palette.neutral.default.seven};

      ${FlippingRow} {
        transform: translateY(-100%);
      }
    }
  }
`

const NameSection = styled(CSSBreakpoint)`
  overflow: hidden;
`

interface Props {
  className?: string
}

function NavBarUserButton(props: Props) {
  const intl = useIntl()
  const account = useAppSelector(state => state.auth.account)
  const currency = useContext(CurrencyContext)
  const isLoyaltyEnabled = useAppSelector(getHasLoyaltyAccount)
  const loyaltyAccount = useAppSelector(state => state.loyalty.account.data)
  const [loyaltyTier] = useCurrentLoyaltyTier()
  const credits = useAppSelector(state => state.auth.account.creditsByCurrency[currency])
  const loyaltyPoints = loyaltyAccount?.points ?? 0

  const showModal = useContext(ModalContext)
  const openAccountDrawer = useCallback(() => {
    showModal(<AccountMenuDrawer />)
  }, [showModal])

  return <Button
    onClick={openAccountDrawer}
    className={props.className}
    data-testid="nav-account"
  >
    <NavBarUserAvatar />
    <NameSection min="desktop">
      <BodyText variant="medium" wrap="truncate" colour="neutral-one">
        {account.givenName || intl.formatMessage({
          id: 'yruRQ+7AtF',
          defaultMessage: 'Account',
          description: 'Fallback display name in navigation when user given name is not available',
        })}
      </BodyText>
      {isLoyaltyEnabled && !!loyaltyTier && (
        <>
          {!!loyaltyPoints && <FlippingContainer>
            <FlippingRow variant="small" wrap="truncate" colour="neutral-two">
              <FormatLoyaltyPoints abbreviated points={loyaltyPoints} />
            </FlippingRow>
            <FlippingRow variant="small" format="uppercase" weight="bold" colour={`tier-${loyaltyTier}-dark`}>
              {getLoyaltyTierDisplayName(loyaltyTier)}
            </FlippingRow>
          </FlippingContainer>}
          {!loyaltyPoints && (
            <BodyText variant="small" format="uppercase" weight="bold" wrap="truncate" colour={`tier-${loyaltyTier}-dark`}>
              {getLoyaltyTierDisplayName(loyaltyTier)}
            </BodyText>
          )}
        </>
      )}

      {!!credits?.balance && !isLoyaltyEnabled && (
        <BodyText variant="small" colour="neutral-two">
          <FormatCurrency value={credits.balance} />
        </BodyText>
      )}
    </NameSection>
  </Button>
}

export default React.memo(NavBarUserButton)
